import React, { useState, useEffect } from 'react';
import './styles.css';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHome, faUser, faList, faFloppyDisk, faXmark  } from "@fortawesome/free-solid-svg-icons";
import FloatingActions, { FloatingAction } from '../FloatingActions';
import Card from '../Card';
import ItemListEdit from '../ItemListEdit';
import { ItemRowValue, Option } from '../ItemRow';
import "./styles.css";
import useRouter from '@/store/useRouter';
import { RouterStoreItemKey } from '@/store/useRouter/types';
import { useShallow } from "zustand/shallow";
import useAppData from '@/store/useAppData';
import axios from "axios";
import { StockMovement } from '@/Utils/constants';
import DialogWindow from '../DialogWindow';
import ItemListSelect from '../ItemListSelect';

const StockingCard: React.FC = () => {

    const selectedStock = useAppData(state => state.selectedStock);
    const loggedUser = useAppData(state => state.user);

    const router = useRouter(
        useShallow(state => ({
            go: state.go,
            history: state.history
        }))
    );

    const actions: FloatingAction[] = [
        {
            key: "3",
            label: "Výběr z existujících položek",
            icon: <FontAwesomeIcon icon={faList } />,
            onClick: () => {setShowStockItemsDialog(true)}
        },
        {
            key: "3",
            label: "Zrušit",
            icon: <FontAwesomeIcon icon={faXmark} />,
            onClick: () => router.go({ key: RouterStoreItemKey.LISTITEMS })
        },
        {
            key: "2",
            label: "Uložit a pokračovat",
            icon: <FontAwesomeIcon icon={faFloppyDisk} />,
            onClick: () => saveAndContinue()
        },
        {
            key: "1",
            label: "Uložit",
            icon: <FontAwesomeIcon icon={faFloppyDisk} />,
            onClick: () => save()
        }
    ];

    const [stockingItems, setStcokingItems] = React.useState<ItemRowValue[]>([]);
    const [list1Items, setList1Items] = React.useState<Option[]>([]);
    const [list2Items, setList2Items] = React.useState<Option[]>([]);
    const [list3Items, setList3Items] = React.useState<Option[]>([]);
    const [stateItems, setStateItems] = React.useState<Option[]>([]);
    const [showDialog, setShowDialog] = useState<boolean>(false);
    const [dialogText, setDialogText] = useState<string>("");
    const [showStockItemsDialog, setShowStockItemsDialog] = useState<boolean>(false);

    const SCAN_CHAR_MAX_INTERVAL = 40;
    const SCAN_SEQUENCE_TIMEOUT = 120;
    const SCAN_MIN_LENGTH = 1;

    type ScanType = "rfid" | "barcode" | "unknown";

    const scanBufferRef = React.useRef<string>("");
    const scanTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    const lastKeyTimeRef = React.useRef<number>(0);
    const lastCharRef = React.useRef<string>("");
    const isScannerSequenceRef = React.useRef<boolean>(false);
    const scannerTargetRef = React.useRef<EventTarget | null>(null);
    const scannerInitialValueRef = React.useRef<string | null>(null);

    useEffect(() => {
        if (!selectedStock) return;

        const loadListsItems = async () => {
            const list1Items = await loadListItems(selectedStock.typeid1, selectedStock.id);
            const list2Items = await loadListItems(selectedStock.typeid2, selectedStock.id);
            const list3Items = await loadListItems(selectedStock.typeid3, selectedStock.id);
            const stateItems = await loadListItems(7, selectedStock.id);

            setList1Items(list1Items);
            setList2Items(list2Items);
            setList3Items(list3Items);
            setStateItems(stateItems);
        };

        void loadListsItems();
    }, [selectedStock]);

    const loadStockItemById = async (stockItemId: string) => {
        try {
            const res = await axios({
                method: "post",
                url: "/api/stockItem/get",
                data: {
                    stockItemId
                },
            });

            if (res.data.stockItem.length > 0) {
                const stockItem = res.data.stockItem[0];
                stockItem.quantity = 1;
                setStcokingItems((prev) => [...prev, stockItem]);
            }
        } catch (err) {
            console.error("Failed to load stock item", err);
            throw err;
        }
    };

    const handleBarcodeScan = async (itemId: number) => {
        try {
            await loadStockItemById(String(itemId));
            console.log("Barcode (QR) scanned:", itemId);
        } catch (err) {
            console.error("Failed to handle barcode scan", err);
        }
    };

    const handleScanCompleted = async (rawCode: string) => {
        let code = rawCode.trim();
        code = fixChipCode(code);
        if (!code) return;

        const type = classifyScan(code);
        console.log("Scan done:", { code, type });

        switch (type) {
            case "rfid":
                break;
            case "barcode":
                await handleBarcodeScan(parseInt(code, 10));
                break;
            case "unknown":
            default:
                console.warn("Unknown scan format:", code);
                break;
        }
    };

    useEffect(() => {
        const handleGlobalKeyDown = (e: KeyboardEvent) => {
            const key = e.key;

            // zajímají nás jen "znakové" klávesy
            if (key.length !== 1) {
                lastCharRef.current = "";
                return;
            }

            const now = performance.now();
            const diff = now - lastKeyTimeRef.current;
            lastKeyTimeRef.current = now;

            const target = e.target as HTMLInputElement | HTMLTextAreaElement | HTMLElement;

            if (!isScannerSequenceRef.current) {
                // ještě nejsme ve scanner režimu

                // pokud je interval mezi minulým znakem a tímto hodně malý → scanner
                if (diff > 0 && diff < SCAN_CHAR_MAX_INTERVAL && lastCharRef.current) {
                    isScannerSequenceRef.current = true;
                    scannerTargetRef.current = target;

                    // uložíme si původní hodnotu inputu (bez prvního scanner znaku)
                    if (target && "value" in target) {
                        const t = target as HTMLInputElement | HTMLTextAreaElement;
                        const currentValue = t.value;
                        // odebereme poslední znak (ten první scanner znak), pokud sedí
                        const initial =
                            currentValue.endsWith(lastCharRef.current)
                                ? currentValue.slice(0, -lastCharRef.current.length)
                                : currentValue;

                        scannerInitialValueRef.current = initial;
                        t.value = initial;
                    } else {
                        scannerInitialValueRef.current = null;
                    }

                    // buffer = předchozí znak + aktuální
                    scanBufferRef.current = lastCharRef.current + key;

                    e.preventDefault();
                    e.stopPropagation();
                } else {
                    // běžné psaní – jen si uložíme poslední znak a necháme projít
                    lastCharRef.current = key;
                    return;
                }
            } else {
                // už jsme ve scanner režimu – všechny znaky patří do bufferu
                e.preventDefault();
                e.stopPropagation();
                scanBufferRef.current += key;
            }

            // Reset timeoutu pro dokončení skenu
            if (scanTimerRef.current) {
                clearTimeout(scanTimerRef.current);
            }

            scanTimerRef.current = setTimeout(() => {
                const fullCode = scanBufferRef.current;
                scanBufferRef.current = "";
                isScannerSequenceRef.current = false;

                // vrátíme původní hodnotu inputu
                const t = scannerTargetRef.current as any;
                if (t && scannerInitialValueRef.current !== null && "value" in t) {
                    t.value = scannerInitialValueRef.current;
                }

                scannerTargetRef.current = null;
                scannerInitialValueRef.current = null;
                lastCharRef.current = "";

                if (fullCode.length >= SCAN_MIN_LENGTH) {
                    void handleScanCompleted(fullCode);
                }
            }, SCAN_SEQUENCE_TIMEOUT);
        };

        window.addEventListener("keydown", handleGlobalKeyDown);

        return () => {
            window.removeEventListener("keydown", handleGlobalKeyDown);
        };
    }, []);

    const classifyScan = (rawCode: string): ScanType => {
        const code = rawCode.trim();

        if (!code) return "unknown";

        if (/^\d{1,5}$/.test(code)) {
            const n = parseInt(code, 10);
            if (n >= 1 && n <= 50000) {
                return "barcode";
            }
        }

        if (/^[\p{L}0-9+]+$/u.test(code)) {
            return "rfid";
        }

        return "unknown";
    };

    const loadListItems = async (listId: number, stockId: string): Promise<Option[]> => {
        try {
            const res = await axios({
                method: "post",
                url: "/api/listItems/getListItems",
                data: {
                    listId,
                    stockId,
                },
            });

            return res.data.listItems.map((listItem: any): Option => ({
                value: listItem.id,
                label: listItem.name,
            }));
        } catch (err) {
            console.error("Failed to load list items", err);
            throw err;
        }
    };

    const handleUpdatedItems = (index: number, item: ItemRowValue) => {
        if (index === -1) {
            setStcokingItems((prev) => [...prev, item]);
            return;
        }

        if (!item) {
            setStcokingItems((prev) => prev.filter((_, i) => i !== index));
            return;
        }

        setStcokingItems((prev) =>
            prev.map((row, i) => (i === index ? item : row))
        );
    };

    const saveAndContinue = async () => {

        if (stockingItems && stockingItems.length > 0) {
            const notInsertedStockItems = [];

            for (const stockingItem of stockingItems) {
                console.log("StockingItem15");
                console.log(stockingItem);

                if (stockingItem?.id) {
                    await updateItem(stockingItem);
                    await stockMovement(selectedStock.id, stockingItem?.id, null, String(StockMovement.StockIn), stockingItem.quantity, loggedUser.id);
                } else {
                    const stockItemByTypes = { ...stockingItem, stockId: selectedStock.id };
                    const exisitingStockItem = await getStockItemByTypes(stockItemByTypes, stockingItem.stateId);

                    if (!exisitingStockItem) {
                        const result = await insertItem(stockingItem);
                        await stockMovement(selectedStock.id, result, null, String(StockMovement.StockIn), stockingItem.quantity, loggedUser.id);
                    } else {
                        notInsertedStockItems.push(stockingItem);
                    }
                }
            }

            if (notInsertedStockItems.length > 0) {
                setStcokingItems(notInsertedStockItems);

                setDialogText("Některé položky nejde vytvořit, protože již na skladě existují");
                setShowDialog(true);
                return notInsertedStockItems;
            } else {
                setStcokingItems([]);
                return [];
            }
        }
    };

    const save = async () => {

        if (stockingItems && stockingItems.length > 0) {
            const notInsertedItems = await saveAndContinue();

            if (notInsertedItems.length === 0) {
                router.go({ key: RouterStoreItemKey.LISTITEMS });
            }
        }
    };

    const insertItem = async (item: ItemRowValue) => {
        try {
            const res = await axios({
                method: "post",
                url: "/api/stockitem/insert",
                data: {
                    name: item.name,
                    stateId: item.stateId,
                    list1Id: item.list1Id,
                    list2Id: item.list2Id,
                    list3Id: item.list3Id,
                    quantity: item.quantity,
                    stockId: selectedStock.id
                },
            });

            return res.data.id;
        } catch (err) {
            console.error("Failed to insert stock item", err);
            throw err;
        }
    };

    const updateItem = async (item: ItemRowValue) => {
        try {
            const res = await axios({
                method: "post",
                url: "/api/stockitem/updateIncrement",
                data: {
                    id: item.id,
                    name: item.name,
                    stateId: item.stateId,
                    list1Id: item.list1Id,
                    list2Id: item.list2Id,
                    list3Id: item.list3Id,
                    quantity: item.quantity,
                },
            });

            return res.data.id;
        } catch (err) {
            console.error("Failed to updateIncrement stock item", err);
            throw err;
        }
    };

    const getStockItemByTypes = async (stockItem: ItemRowValue, stateId: string): Promise<ItemRowValue | null> => {
        try {
            const res = await axios({
                method: "post",
                url: "api/stockitem/getByTypes",
                data: {
                    stateId: stateId,
                    list1Id: stockItem.list1Id,
                    list2Id: stockItem.list2Id,
                    list3Id: stockItem.list3Id,
                    stockId: stockItem.stockId,
                    name: stockItem.name
                },
            });

            if (res.data.stockItem && res.data.stockItem.length > 0) {
                return res.data.stockItem[0];
            } else {
                return null;
            }

        } catch (err) {
            console.error("Failed to load getByTypes", err);
            throw err;
        }
    };

    const createListItem = async (listItemName: string, listId: number, stockId: string) => {
        try {
            const res = await axios({
                method: "post",
                url: "/api/listItems/createListItem",
                data: {
                    name: listItemName,
                    listId: listId,
                    stockId: stockId,
                    active: true
                },
            });

            return res.data.id;
        } catch (err) {
            console.error("Failed to insert list item", err);
            throw err;
        }
    };

    const stockMovement = async (stockId: string, stockItemId: string, objectId: string, stockMovementId: string, quantity: number, userId: string) => {
        try {
            const res = await axios({
                method: "post",
                url: "/api/log/stockMove",
                data: {
                    stockId: stockId,
                    stockItemId: stockItemId,
                    objectId: objectId,
                    quantity: quantity,
                    stockMovementId: stockMovementId,
                    userId: userId
                },
            });

            return res.data.id;
        } catch (err) {
            console.error("Failed to make stock movement item", err);
            throw err;
        }
    };

    const fixChipCode = (str) => {
        if (!str) {
            return str;
        }

        const charMap = {
            "é": "0",
            "+": "1",
            "ě": "2",
            "š": "3",
            "č": "4",
            "ř": "5",
            "ž": "6",
            "ý": "7",
            "á": "8",
            "í": "9"
        };

        return str.split("").map(char => charMap[char] || char).join("");
    }

    const createList1Item = async (listItemValue: string) => {
        await createListItem(listItemValue, selectedStock.typeid1, selectedStock.id);

        const list1Items = await loadListItems(selectedStock.typeid1, selectedStock.id);
        setList1Items(list1Items);
    }

    const createList2Item = async (listItemValue: string) => {
        await createListItem(listItemValue, selectedStock.typeid2, selectedStock.id);

        const list2Items = await loadListItems(selectedStock.typeid2, selectedStock.id);
        setList2Items(list2Items);
    }

    const createList3Item = async (listItemValue: string) => {
        await createListItem(listItemValue, selectedStock.typeid3, selectedStock.id);

        const list3Items = await loadListItems(selectedStock.typeid3, selectedStock.id);
        setList3Items(list3Items);
    }

    const onSelectStockItem = async (stockItemId: string) => {
        if(!stockItemId){
            setShowStockItemsDialog(false);
            return;
        }

        try {
            await loadStockItemById(stockItemId);
            setShowStockItemsDialog(false);
        } catch (err) {
            console.error("Failed to select stock item", err);
        }
    }

    return <>
    {showStockItemsDialog && (
            <div className="inv-modal-overlay1" onMouseDown={() => setShowStockItemsDialog(false)}>
                <div
                    className="inv-modal1"
                    onMouseDown={(e) => e.stopPropagation()}
                    role="dialog"
                    aria-modal="true"
                >
                    <ItemListSelect onSave={onSelectStockItem}></ItemListSelect>
                </div>
            </div>
        )}
        {showDialog &&
            <DialogWindow
                displayDialog={showDialog}
                setDisplayDialog={setShowDialog}
                title={dialogText}
                cancelText={"OK"}
            />
        }
        <Card
            title="Příjem na sklad"
        >
            <div className="item-list-stack">

                <ItemListEdit
                    header='Naskladňované položky'
                    list1Options={list1Items}
                    list2Options={list2Items}
                    list3Options={list3Items}
                    itemStateOptions={stateItems}
                    onAddCollection={createList1Item}
                    onAddGender={createList2Item}
                    onAddSize={createList3Item}
                    displayAddListItem={true}
                    items={stockingItems}
                    onChange={handleUpdatedItems}
                />

            </div>

            {actions?.length ? (
                <div className="item-list-actions">
                    <FloatingActions actions={actions} />
                </div>
            ) : null}
        </Card></>;
};

export default StockingCard;